import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  IconButton,
  Typography,
  Skeleton,
  Alert,
  Button,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import axiosInstance from '../../lib/axios';
import { CustomerHistoryView } from '../../components/customers';
import { Customer } from '../../types/customer';

const fetchCustomer = async (id: string): Promise<Customer> => {
  const response = await axiosInstance.get(`/customers/${id}`);
  return response.data;
};

export const CustomerHistoryPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: customer, isLoading, error } = useQuery({
    queryKey: ['customer', id],
    queryFn: () => fetchCustomer(id!),
    enabled: !!id,
  });

  if (!id) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Customer ID is required</Alert>
        <Button
          variant="contained"
          onClick={() => navigate('/app/customers')}
          sx={{ mt: 2 }}
        >
          Back to Customers
        </Button>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
        <Box display="flex" alignItems="center" gap={2} mb={3}>
          <IconButton onClick={() => navigate(`/app/customers/${id}`)}>
            <ArrowBackIcon />
          </IconButton>
          <Skeleton variant="text" width={200} height={40} />
        </Box>
        <Skeleton variant="rectangular" height={200} sx={{ mb: 3 }} />
        <Skeleton variant="rectangular" height={400} />
      </Box>
    );
  }

  if (error || !customer) {
    return (
      <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
        <Box display="flex" alignItems="center" gap={2} mb={3}>
          <IconButton onClick={() => navigate('/app/customers')}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5">Customer Not Found</Typography>
        </Box>
        <Alert severity="error">
          Unable to load customer. The customer may not exist or you may not have permission to view them.
        </Alert>
        <Button
          variant="contained"
          onClick={() => navigate('/app/customers')}
          sx={{ mt: 2 }}
        >
          Back to Customers
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      {/* Header */}
      <Box display="flex" alignItems="center" gap={2} mb={3}>
        <IconButton onClick={() => navigate(`/app/customers/${id}`)}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5">
          Customer History: {customer.firstName} {customer.lastName}
        </Typography>
      </Box>

      {/* History View */}
      <CustomerHistoryView
        customerId={id}
        customer={customer}
        currencySymbol="DZD "
      />
    </Box>
  );
};

export default CustomerHistoryPage;
